<template>
    <div class="ws-list animated fadeIn">
        <!-- Page Header -->
        <div class="ws-page-header">
            <div class="ws-header-content">
                <div class="ws-header-icon">
                    <b-icon icon="columns-gap"></b-icon>
                </div>
                <div class="ws-header-text">
                    <h1 class="ws-page-title">{{ $t('workspaces') }}</h1>
                    <p class="ws-page-subtitle">{{ $t('workspaceDescription') }}</p>
                </div>
            </div>
            <b-button class="ws-create-btn" to="/admin/workspace/create">
                <b-icon icon="plus-lg" class="mr-2"></b-icon>
                {{ $t('createNew') }}
            </b-button>
        </div>

        <!-- Empty State -->
        <div v-if="workspaces.length === 0" class="ws-empty-state">
            <div class="ws-empty-icon">
                <b-icon icon="columns-gap" font-scale="3"></b-icon>
            </div>
            <h3 class="ws-empty-title">{{ $t('noWorkspaces') }}</h3>
            <p class="ws-empty-description">{{ $t('noWorkspacesDescription') }}</p>
            <b-button class="ws-create-btn" to="/admin/workspace/create">
                <b-icon icon="plus-lg" class="mr-2"></b-icon>
                {{ $t('createNew') }}
            </b-button>
        </div>

        <!-- Workspace Grid -->
        <div v-else class="ws-grid">
            <div
                v-for="(workspace, index) in workspaces"
                :key="workspace.uuid"
                class="ws-card"
                @click="edit(workspace)"
            >
                <div :class="'ws-card-icon ws-icon-' + (index % 6)">
                    <span class="ws-initial">{{ getInitial(workspace.name) }}</span>
                </div>
                <div class="ws-card-body">
                    <h3 class="ws-card-name">{{ workspace.name }}</h3>
                </div>
                <div class="ws-card-arrow">
                    <b-icon icon="chevron-right"></b-icon>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.ws-list {
    padding: 0;
}

/* ── Page Header ───────────────────────────────────────────────────── */
.ws-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
    gap: 1rem;
}

.ws-header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.ws-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%);
    flex-shrink: 0;

    svg {
        width: 22px;
        height: 22px;
        color: #fff;
    }
}

.ws-page-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.2rem;
    line-height: 1.2;
}

.ws-page-subtitle {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin: 0;
}

.ws-create-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    color: #fff;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    transition: all 0.25s ease;
    white-space: nowrap;
    text-decoration: none;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        color: #fff;
        text-decoration: none;
    }
    &:active {
        transform: translateY(0);
    }
}

/* ── Empty State ───────────────────────────────────────────────────── */
.ws-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 16px;
}

.ws-empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(94, 231, 223, 0.15) 0%, rgba(180, 144, 202, 0.15) 100%);
    border: 1px solid rgba(94, 231, 223, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.25rem;
    color: var(--text-secondary);
}

.ws-empty-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 0.5rem;
}

.ws-empty-description {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin: 0 0 1.5rem;
}

/* ── Grid ──────────────────────────────────────────────────────────── */
.ws-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
}

/* ── Card ──────────────────────────────────────────────────────────── */
.ws-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.1rem 1.25rem;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
        border-color: var(--border-hover);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);

        .ws-card-arrow {
            opacity: 1;
            transform: translateX(0);
        }
    }

    &:active {
        transform: translateY(0);
    }
}

.ws-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ws-initial {
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
    line-height: 1;
}

.ws-icon-0 { background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%); }
.ws-icon-1 { background: linear-gradient(135deg, #6c80ff 0%, #764ba2 100%); }
.ws-icon-2 { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
.ws-icon-3 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.ws-icon-4 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.ws-icon-5 { background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%); }

.ws-card-body {
    flex: 1;
    min-width: 0;
}

.ws-card-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ws-card-arrow {
    color: var(--text-secondary);
    opacity: 0;
    transform: translateX(-4px);
    transition: all 0.2s ease;
    flex-shrink: 0;
}
</style>

<script>
export default {
    name: "WorkspaceList",
    props: {},
    data: function() {
        return {}
    },
    computed: {
        workspaces() {
            return this.$store.getters.getCurrentProject.workspaces;
        }
    },

    methods: {
        edit(workspace) {
            this.$router.push({path: "/admin/workspace/edit/" + workspace.uuid});
        },

        getInitial(name) {
            if (!name || name.length === 0) return '?'
            return name[0].toUpperCase()
        }
    }
}
</script>
